import { api } from '@/modules/shared/lib/axios'
import type { ResponseAPI } from '@/modules/shared/types/response-api.type'
import type { Promotion } from '../types/promotion.type'

export type PromotionVisitSearchPayload = {
  filter: string
  inputValue: string
  inputValue2?: string // Optional second parameter for date ranges
  filterType: 'igual' | 'contendo'
}

export type PromotionVisitSearchResponse = {
  promotions: Promotion[]
  totalRecords: number
  hasMore: boolean
}

class PromotionVisitSearchService {
  private readonly ITEMS_PER_PAGE = 15

  private buildBaseUrl(payload: PromotionVisitSearchPayload): string {
    // If we have a second input value (e.g., date range), include it in the URL
    if (payload.inputValue2) {
      return `promotorvisita/online/${payload.filterType}/${payload.filter}/${payload.inputValue}/${payload.inputValue2}`
    }
    return `promotorvisita/online/${payload.filterType}/${payload.filter}/${payload.inputValue}`
  }

  async getTotalRecords(payload: PromotionVisitSearchPayload): Promise<number> {
    try {
      const url = this.buildBaseUrl(payload)
      const response = await api.get<ResponseAPI>(url)

      if (response.data?.body) {
        const parsedBody = JSON.parse(response.data.body)

        // Correção para o formato de resposta: {"status":200,"message":"","body":"[{\"records\":4}]"}
        if (Array.isArray(parsedBody) && parsedBody.length > 0 && parsedBody[0].records) {
          return parsedBody[0].records
        }
        return 0
      }
      return 0
    } catch (error) {
      console.error('Erro ao buscar total de registros de visitas:', error)
      throw error
    }
  }

   async getPromotionsPage(payload: PromotionVisitSearchPayload, page: number): Promise<Promotion[]> {
    try {
      const url = this.buildBaseUrl(payload)
      const offset = (page - 1) * this.ITEMS_PER_PAGE
      const response = await api.get(`${url}?offset=${offset}&limit=${this.ITEMS_PER_PAGE}`)

      if (response.data?.body) {
        const promotions = JSON.parse(response.data.body)
        return Array.isArray(promotions) ? promotions : []
      }
      return []
    } catch (error) {
      console.error('Erro ao buscar visitas promocionais:', error)
      throw error
    }
  }

  async searchInitialPromotions(payload: PromotionVisitSearchPayload): Promise<PromotionVisitSearchResponse> {
    const totalRecords = await this.getTotalRecords(payload)

    if (totalRecords === 0) {
      return { promotions: [], totalRecords: 0, hasMore: false }
    }

    const promotions = await this.getPromotionsPage(payload, 1)
    const hasMore = totalRecords > this.ITEMS_PER_PAGE

    return { promotions, totalRecords, hasMore }
  }

  async loadMorePromotions(
    payload: PromotionVisitSearchPayload,
    page: number,
    currentTotal: number,
    totalRecords: number
  ): Promise<{ promotions: Promotion[], hasMore: boolean }> {
    console.log('📥 Carregando mais visitas:', {
      page,
      currentTotal,
      totalRecords,
      shouldLoad: currentTotal < totalRecords
    })

    // Verifica se já carregou todos os registros
    if (currentTotal >= totalRecords) {
      console.log('🛑 Todas as visitas já foram carregadas')
      return { promotions: [], hasMore: false }
    }

    const promotions = await this.getPromotionsPage(payload, page)
    const newTotal = currentTotal + promotions.length
    const hasMore = promotions.length === this.ITEMS_PER_PAGE && newTotal < totalRecords

    console.log('✅ Mais visitas carregadas:', {
      newPromotions: promotions.length,
      newTotal,
      totalRecords,
      hasMore
    })

    return { promotions, hasMore }
  }

  // Método para buscar visitas de uma data específica
  async searchPromotionsByDate(date: string): Promise<PromotionVisitSearchResponse> {
    return this.searchInitialPromotions({
      filter: 'data',
      inputValue: date,
      filterType: 'igual'
    })
  }

  // Método para buscar visitas por intervalo de datas
  async searchPromotionsByDateRange(startDate: string, endDate: string): Promise<PromotionVisitSearchResponse> {
    return this.searchInitialPromotions({
      filter: 'data',
      inputValue: startDate,
      inputValue2: endDate,
      filterType: 'igual'
    })
  }

  // Método para buscar visitas por nome do promotor
  async searchPromotionsByPromoterName(promoterName: string): Promise<PromotionVisitSearchResponse> {
    return this.searchInitialPromotions({
      filter: 'p.razao_social_nome',
      inputValue: promoterName,
      inputValue2: undefined,
      filterType: 'contendo'
    })
  }

  // Método para buscar todas as visitas por razão social (carregamento inicial)
  async searchAllPromotionsByRazaoSocial(): Promise<PromotionVisitSearchResponse> {
    return this.searchInitialPromotions({
      filter: 'p.razao_social_nome',
      inputValue: '%20', // Espaço codificado para trazer registros com espaços no nome
      inputValue2: undefined,
      filterType: 'contendo'
    })
  }

  // Método para buscar visitas por cliente
  async searchPromotionsByClient(clientId: string): Promise<PromotionVisitSearchResponse> {
    return this.searchInitialPromotions({
      filter: 'cliente',
      inputValue: clientId,
      inputValue2: undefined,
      filterType: 'igual'
    })
  }

  // Método para buscar visitas por ID
  async searchPromotionsById(visitId: string): Promise<PromotionVisitSearchResponse> {
    return this.searchInitialPromotions({
      filter: 'id_visita',
      inputValue: visitId,
      inputValue2: undefined,
      filterType: 'igual'
    })
  }
}

export const promotionVisitSearchService = new PromotionVisitSearchService()